<template>
  <div
    class="meeting-planning-item generic-border"
    :class="{ expand: isExpanded || isLoaded }"
  >
    <div class="v-centerer meeting-planning-item__title" @click="openPlanning">
      <Icon :rotate-left="!isExpanded" class="meeting-planning-item__title__expand">
        {{ arrowDownLine }}
      </Icon>
      <EditableLabel
        v-model="mpCopy.name"
        class="meeting-planning-item__title__label"
        :placeholder="$t('message.MEETING_NAME')"
        use-textarea
        @input="save()"
      />
      <button
        class="generic-border generic-border--icon-button generic-border--red meeting-planning-item__title__delete"
        @click.stop="deletePlanning"
      >
        <Icon>{{ deleteMedium }}</Icon>
      </button>
    </div>

    <div v-if="isExpanded" class="meeting-planning-item__content">
      <div class="generic-background">
        <div class="generic-border meeting-planning-item__content__details">
          <EditableLabelRadio
            v-if="!fromConfig"
            v-model="mpCopy.participantIds"
            :items="userItems"
            :legend="$t('message.PARTICIPANTS') + $t('message.TWO_DOTS')"
            label-key="fullName"
            separator=", "
            multiple
            @input="save()"
          />
          <EventPlanning ref="evtPlanning" :value="mpCopy" @input="save" />
        </div>
        <div class="meeting-planning-item__content__steps">
          <div class="meeting-planning-item__content__steps__header">
            <span class="legend">{{ $t("message.STEPS") }}</span>
            <span style="margin-left: 50px;">{{ getTotalDuration }}</span>
          </div>

          <MeetingStep
            v-for="(step, index) in mpCopy.stepsData"
            :key="step.id"
            :index="index"
            :value="step"
            :from-config="fromConfig"
            @removeStep="removeStep(step)"
            @input="saveStep(step)"
          />
          <button
            class="generic-border generic-border--text-btn v-centerer meeting-planning-item__content__steps__add"
            @click="addStep"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ $t("message.ADD_STEP") }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="isLoaded" class="meeting-planning-item__loading">
      {{ $t('message.LOADING_DOTS') }}
    </div>
  </div>
</template>

<script>
/**
 * Component used to setup meeting plannings of a team or the template plannings in the config
 *
 * @vue-prop {Object} meetingPlanning - Object to edit
 * @vue-prop {Boolean} fromConfig - True if editing a meeting template
 */


import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import $ from 'jquery';
// Import components
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EventPlanning from '@/components/event-planning.vue';
import Icon from '@/components/icon.vue';
import MeetingStep from '@/components/meeting-step.vue';
// Import utils
import { GET_TEAMS } from '@/store/mutation-types';
import {
  DELETE_PLANNING,
  SET_PLANNING,
  SET_PLANNING_STEP,
  DELETE_PLANNING_STEP,
} from '@/store/modules/meeting/action-types';
import { calert, preventPastingStyles, secondsToArray } from '@/utils/main';
import { addExtraBold, arrowDownLine, deleteMedium } from '@/assets/icons';


export default {

  name: 'MeetingPlanningItem',

  components: {
    EditableLabel,
    EditableLabelRadio,
    EventPlanning,
    Icon,
    MeetingStep,
  },

  props: {
    meetingPlanning: {
      type: Object,
      required: true,
    },
    fromConfig: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      deleteMedium,
      // Copy of the meeting planning to handle edition properly
      mpCopy: null,
      // Handle sorting steps items
      draggedIndex: 0,
      isExpanded: false,
      isLoaded: false,
    };
  },

  computed: {
    /** Array: Items of the editable label radio to pick users */
    userItems() {
      const currentTeamId = (this.currentTeam ? this.currentTeam.id : null);
      return this.teamsData.map(team => ({
        title: team.name + (team.id === currentTeamId ? ` (${this.$t('message.OUR_TEAM')})` : ''),
        radioItems: team.users.map(user => Object.assign(
          cloneDeep(user),
          { id: Number(user.id), fullName: this.$t('fullNameFormat', user) },
        )),
      }));
    },
    /** String: Total duration of the meeting as human readable string */
    getTotalDuration() {
      let duration = 0;
      for (let i = 0, len = this.mpCopy.stepsData.length; i < len; i++) {
        duration += this.mpCopy.stepsData[i].duration;
      }
      const time = secondsToArray(duration);
      return `${time[0]}:${time[1]}:${time[2]}`;
    },
    ...mapState({
      teamsData: state => state[GET_TEAMS.stateKey] || [],
    }),
  },

  watch: {
    meetingPlanning: {
      deep: true,
      handler() {
        this.mpCopy = cloneDeep(this.meetingPlanning);
      },
    },
  },

  created() {
    this.mpCopy = cloneDeep(this.meetingPlanning);
  },

  beforeDestroy() {
    $('.meeting-planning-item__content__steps').sortable('destroy');
  },

  methods: {
    /** Update the UI features after refreshing the lsit of data */
    afterUpdatePlannings() {
      this.refreshSortable();
      this.$nextTick(preventPastingStyles);
    },
    refreshSortable() {
      this.$nextTick(() => {
        $('.meeting-planning-item__content__steps').sortable({
          items: '.meeting-step',
          axis: 'y',
          opacity: 1,
          placeholder: 'meeting-step-placeholder',
          update: (event, ui) => {
            const planning = this.mpCopy;
            const steps = planning.stepsData;
            const newIndex = ui.item.index() - 1;
            steps.splice(newIndex, 0, steps.splice(this.draggedIndex, 1)[0]);
            const order = steps.map(step => step.id);
            this.$store.dispatch(`meeting/${SET_PLANNING_STEP}`, { id: order[0], planning: planning.id, order })
              .then(this.afterUpdatePlannings);
          },
          start: (event, ui) => {
            this.draggedIndex = ui.item.index() - 1;
          },
        });
      });
    },
    /** Open a planning's details */
    openPlanning() {
      if (!this.isExpanded) {
        this.isLoaded = true;
        // Prevent freezing UI and display a minimalistic 'loading' message
        setTimeout(() => {
          this.isExpanded = true;
          this.refreshSortable();
        }, 1);
      } else {
        this.isExpanded = false;
        this.isLoaded = false;
      }
    },
    // Prompt the user to confirm deleting a planning */
    deletePlanning(planning) {
      calert(this.$t('message.DELETE_PLANNING_CONFIRM'),
        [
          {
            title: this.$t('message.DELETE'),
            action: () => {
              this.confirmDeletePlanning(planning);
            },
            sClass: 'red',
          },
          {
            title: this.$t('message.CANCEL'),
          },
        ]);
    },
    /** Sned the deletion XHR */
    confirmDeletePlanning() {
      this.$store.dispatch(`meeting/${DELETE_PLANNING}`, { id: this.meetingPlanning.id });
    },
    /** Create and add a new step */
    addStep() {
      this.$store.dispatch(`meeting/${SET_PLANNING_STEP}`, { planning: this.meetingPlanning.id })
        .then(this.afterUpdatePlannings);
    },
    /** Edit a step */
    saveStep(step) {
      const params = { ...step, planning: this.meetingPlanning.id };
      this.$store.dispatch(`meeting/${SET_PLANNING_STEP}`, params).then(this.afterUpdatePlannings);
    },
    removeStep(step) {
      const params = { id: step.id, planning: this.meetingPlanning.id };
      this.$store.dispatch(`meeting/${DELETE_PLANNING_STEP}`, params).then(this.afterUpdatePlannings);
    },
    save(planning) {
      planning = planning || this.mpCopy;
      this.$store.dispatch(`meeting/${SET_PLANNING}`, planning).then(() => {
        this.afterUpdatePlannings();
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.meeting-planning-item {
  margin-top: .7rem;

  .settings-item {
    .delete-img {
      margin-left: 1em;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    padding: 1em;
  }

  &__title {
    padding: 1rem;

    &__expand {
      width: .8rem;
      margin-right: 1.5rem;
    }

    &__label {
      flex: 1;
      min-width: 0px;
    }

    &__delete {
      flex: 0 0 auto;
      margin-left: 3rem;
    }
  }

  &__content {
    padding: 1rem;

    &__details {
      padding: 1rem .7rem;
    }

    &__steps {
      position: relative;

      &__header {
        margin: 1rem;
      }

      .meeting-step {
        margin-top: 0.7rem;
      }

      &__add {
        margin-top: .7rem;

        &.generic-border--text-btn {
          background: #fff;
        }
      }
    }
  }

  .meeting-step-placeholder {
    height: 6em;
    background: $color-droppable-active;
  }
}
</style>
